import React, { ChangeEvent, FC, useState } from "react";
import styles from "./DateInput.module.css";

interface DateInputProps {
    label?: string;
    value: Date | null; // UTC Date value
    onChange: (value: Date | null) => void; // Return date to server in UTC
    placeholder?: string;
    errorMessage?: string;
    id: string;
    disabled?: boolean;
}

const DateInput: FC<DateInputProps> = ({
    label,
    value,
    onChange,
    placeholder = "",
    errorMessage = "",
    id,
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState<string>(
        value ? new Date(value).toISOString().split("T")[0] : ""
    );

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const dateString = event.target.value;
        setInputValue(dateString); // Update local state for input field

        if (dateString.length === 10) { // Ensure full date is entered (YYYY-MM-DD)
            const [year, month, day] = dateString.split("-").map(Number);

            // Validate parsed numbers
            if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
                const utcDate = new Date(Date.UTC(year, month - 1, day)); // month is zero-indexed

                // Check if the date is valid
                if (!Number.isNaN(utcDate.getTime())) {
                    onChange(utcDate);
                    return;
                }
            }
        }

        // If invalid or incomplete, do not update onChange
        onChange(null);
    };

    const handleReset = () => {
        setInputValue("");
        onChange(null);
    };

    const isValid = !errorMessage;

    // Format the input value in UTC for display (in 'YYYY-MM-DD' format)
    const utcDateString = value
        ? new Date(value).toISOString().split("T")[0]
        : "";

    return (
        <div className={styles.container}>
            {label && <label htmlFor={id} className={styles.label}>{label}</label>}
            <div className={styles.inputWrapper}>
                <input
                    id={id}
                    className={`${styles.dateInput} ${isValid ? "" : styles.invalid}`}
                    type="date"
                    value={inputValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    aria-invalid={!isValid}
                    aria-describedby={!isValid ? `${id}-error` : undefined}
                    disabled={disabled}
                />
                {inputValue && !disabled && (
                    <button
                        type="button"
                        onClick={handleReset}
                        className={styles.resetButton}
                        aria-label="Reset date input"
                    >
                        &#x2715;
                    </button>
                )}
            </div>
            {!isValid && (
                <span id={`${id}-error`} className={styles.errorMessage}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

export default DateInput;
